import type { FormFieldState, FormVariableState } from '../../types/types';
import { createUniqueId } from '../../utils/print';

export const fixedControlHeightClass = 'h-[50px] max-h-[50px]';

export const getPreviewFieldPlaceholder = (fieldType?: string) => {
  switch (fieldType) {
    case 'Double':
    case 'Currency':
      return 'integer value';
    case 'Date':
    case 'CurrentDate':
      return 'date value';
    case 'Time':
      return 'time value';
    default:
      return 'text value';
  }
};

export const formatPreviewTypeLabel = (fieldType?: string) =>
  (fieldType || 'String').replace(/([A-Z])/g, ' $1').trim();

export const createNewVariable = () => ({
  id: createUniqueId(),
  fieldId: '',
  name: '',
  type: '',
  variableRule: '',
  labelFieldMapping: '',
  conditions: [],
  actions: [],
});

export const numericLabelFieldTypes = new Set(['Double', 'Currency']);
export const supportedFieldTypes = new Set([
  'String',
  'Double',
  'Currency',
  'Date',
  'CurrentDate',
  'Time',
]);

export const resolveVariableType = (
  variable: Pick<FormVariableState, 'type'>,
  mappedField?: FormFieldState
): 'Integer' | 'String' => {
  if (variable.type === 'Integer' || variable.type === 'String') {
    return variable.type;
  }

  if (variable.type.trim() && supportedFieldTypes.has(variable.type)) {
    return numericLabelFieldTypes.has(variable.type) ? 'Integer' : 'String';
  }

  if (mappedField && numericLabelFieldTypes.has(mappedField.type)) {
    return 'Integer';
  }

  return 'String';
};

export const resolveFieldType = (
  variable: Pick<FormVariableState, 'type'>,
  mappedField: FormFieldState
): FormFieldState['type'] => {
  if (variable.type.trim() && supportedFieldTypes.has(variable.type)) {
    return variable.type as FormFieldState['type'];
  }

  if (variable.type === 'String') {
    return 'String';
  }

  if (variable.type === 'Integer') {
    return numericLabelFieldTypes.has(mappedField.type) ? mappedField.type : 'Double';
  }

  return mappedField.type;
};

export const isFieldMappedToVariable = (
  field: FormFieldState,
  variable: Pick<FormVariableState, 'fieldId'>
) => Boolean(field.id && variable.fieldId.trim() === field.id);

export const isMissingValue = (value: unknown) => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

export const getMissingRequiredAttachedFieldNames = <
  PropertyKey extends keyof Pick<FormFieldState, 'name'> & keyof Pick<FormVariableState, 'name'>,
>(
  fields: FormFieldState[],
  variables: FormVariableState[],
  property: PropertyKey
) =>
  fields
    .filter((field) => {
      if (!field.required) return false;
      const mappedVariable = variables.find((v) => isFieldMappedToVariable(field, v));
      if (!mappedVariable) return false;
      return isMissingValue(field[property]) && isMissingValue(mappedVariable[property]);
    })
    .map((field) => field.name);

export const hasVariableWithoutFieldAttachment = (
  variables: FormVariableState[],
  fields?: FormFieldState[]
) =>
  variables.some(
    (variable) =>
      !variable.fieldId.trim() ||
      (fields !== undefined && !fields.some((field) => isFieldMappedToVariable(field, variable)))
  );

export const buildRulesFromVariables = (variables: FormVariableState[]) =>
  variables
    .filter((v) => v.actions.length > 0)
    .map((v, i) => {
      const fieldName = v.labelFieldMapping || v.name.trim();
      return {
        name: v.name.trim() || `Rule for variable ${i + 1}`,
        enabled: true,
        priority: i + 1,
        actions: v.actions.map(({ id: _id, type, ...a }) => {
          const config = (a.config ?? {}) as Record<string, unknown>;
          return {
            type: type as 'hide' | 'readonly' | 'set_value' | 'counter',
            config: { ...config, field: fieldName },
          };
        }),
      };
    });

export const buildFieldsPayload = (fields: FormFieldState[], variables: FormVariableState[]) =>
  fields
    .filter((field) => variables.some((variable) => isFieldMappedToVariable(field, variable)))
    .map((field) => {
      const mappedVariable = variables.find((variable) => isFieldMappedToVariable(field, variable));

      const {
        id: _id,
        text: _text,
        initialValue: _initialValue,
        inputFormat: _inputFormat,
        order,
        ...fieldPayload
      } = field;
      const sanitizedOrder = order ?? undefined;

      if (!mappedVariable) {
        return {
          ...fieldPayload,
          order: sanitizedOrder,
          type: fieldPayload.type as
            | 'String'
            | 'Double'
            | 'Currency'
            | 'Date'
            | 'CurrentDate'
            | 'Time',
        };
      }

      const resolvedName = mappedVariable.name.trim() || field.name;

      return {
        ...fieldPayload,
        name: resolvedName,
        order: sanitizedOrder,
        type: (mappedVariable.type.trim()
          ? resolveFieldType(mappedVariable, field)
          : fieldPayload.type) as
          | 'String'
          | 'Double'
          | 'Currency'
          | 'Date'
          | 'CurrentDate'
          | 'Time',
      };
    });

export const buildVariablesPayload = (variables: FormVariableState[], fields: FormFieldState[]) =>
  variables
    .filter((variable) => variable.fieldId.trim())
    .map(({ id: _id, fieldId, conditions: _conditions, actions: _actions, ...variable }) => {
      const mappedField = fields.find((field) => isFieldMappedToVariable(field, { fieldId }));
      const resolvedName = variable.name.trim() || mappedField?.name || '';
      const resolvedLabelFieldMapping = mappedField?.name || variable.labelFieldMapping.trim();

      return {
        ...variable,
        name: resolvedName,
        labelFieldMapping: resolvedLabelFieldMapping,
        type: resolveVariableType(variable, mappedField),
        variableRule: variable.variableRule.trim() || null,
      };
    });

/**
 * Parse API form data (variables + rules) into local FormVariableState[].
 * Extracted to reduce cognitive complexity of the editor hook.
 */
export const parseFormVariablesFromApi = (
  apiData: {
    variables?: Array<Record<string, unknown>>;
    rules?: Array<Record<string, unknown>>;
  },
  createId: () => string
): FormVariableState[] => {
  const apiVariables: FormVariableState[] = (apiData.variables ?? []).map(
    (v: Record<string, unknown>) => ({
      id: createId(),
      fieldId: '',
      name: (v.name as string) ?? '',
      type: (v.type as string) ?? '',
      variableRule: (v.variableRule as string) ?? '',
      labelFieldMapping: (v.labelFieldMapping as string) ?? '',
      conditions: [],
      actions: [],
    })
  );

  for (const rule of (apiData.rules ?? []) as Array<Record<string, unknown>>) {
    const ruleActions = (rule.actions ?? []) as Array<Record<string, unknown>>;
    const ruleConditions = (rule.conditions ?? []) as Array<Record<string, unknown>>;

    const ruleFieldRef =
      (ruleActions[0]?.config as Record<string, unknown> | undefined)?.field ??
      (ruleConditions[0]?.value as Record<string, unknown> | undefined)?.field;

    const ruleName = rule.name as string | undefined;
    const varMatch = apiVariables.find(
      (v) =>
        (typeof ruleFieldRef === 'string' &&
          ruleFieldRef.length > 0 &&
          (v.labelFieldMapping === ruleFieldRef || v.name === ruleFieldRef)) ||
        v.name === ruleName ||
        v.name === ruleName?.replace(/^Rule for /, '')
    );

    if (varMatch) {
      varMatch.conditions = ruleConditions.map((c) => ({
        id: createId(),
        type: (c.type as string) ?? 'equals',
        operator: c.operator as string | undefined,
        value: c.value,
      }));
      varMatch.actions = ruleActions.map((a) => ({
        id: createId(),
        type: (a.type as string) ?? 'block',
        config: a.config as Record<string, unknown> | undefined,
      }));
    }
  }

  return apiVariables;
};
